import React, {useState, useEffect} from 'react';
import type {
  UserModelV2,
  StandardSetCreatorModelV1,
  CurriculumAreaModelV1
} from '@albert-io/models';
import {resource} from '@albert-io/json-api-framework/request/builder';
import {invalidatePartialInterest} from 'resources/mandark.resource';
import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import sessionStore from 'client/Session/SessionStore';
import classnames from 'classnames';

import apolloClient from 'apollo/client';

import {
  Text,
  Select,
  Button,
  Checkbox,
  Banner,
  addGenericErrorToast,
  addToast,
  Heading
} from '@albert-io/atomic';
import {getCardClassName} from '@albert-io/atomic/utils/getCardClassName';

import {List} from 'immutable';

import {usePerformSignupStep} from 'client/SignUp/NewSignUp/usePerformSignupStep';

import {getUserState} from './ContentPersonalization.util';
import './content-personalization.scss';

// default ids to preselect for users who are onboarding
const ONBOARDING_DEFAULT_CHECKED_IDS = [
  '96ffccd1-450f-43f7-a15c-160bd34f6348', // ACT® WorkKeys
  'fdc31a6c-0651-4c1f-82ae-4dfade4e6f48', // College Board (SAT®)
  'b274fd95-6d13-410b-b9c2-ca4515b693de', // ACT®
  'ae3994ee-2392-4469-98a7-37eccab9d5d1', // College Board (Pre-AP®)
  '8aa036cb-5b93-40e6-8b52-3de9d9305496' // College Board (AP®)
];

interface Props extends PropsWithClassNameRequired {
  user: UserModelV2;
  isOnboarding: boolean;
  onComplete: () => void; // for onboarding only, not used in settings menu. see ForceOnboarding.react.js
}

const allStandardSetCreatorsQuery = resource('standard_set_creators_v1')
  .mandarkEndpoint(['standard_set_creators_v1'])
  .pageSize(200);

const allCurriculumAreasQuery = resource('curriculum_areas_v1')
  .mandarkEndpoint(['curriculum_areas_v1'])
  .pageSize(200);

const ContentPersonalization = ({
  user,
  isOnboarding = false,
  onComplete: onOnboardingComplete = () => {}
}: Props) => {
  const performSignupStep = usePerformSignupStep();
  // all standard creators fetched from server to be displayed
  const [statePreferences, setStatePreferences] = useState<List<StandardSetCreatorModelV1>>(List());
  const [nationalPreferences, setNationalPreferences] =
    useState<List<StandardSetCreatorModelV1>>(List());

  // all curriculum areas fetched from server to be displayed
  const [curriculumAreaPreferences, setCurriculumAreaPreferences] =
    useState<List<CurriculumAreaModelV1>>(List());

  // IDs of the actual standard creators the user has selected
  const [userNationalIds, setUserNationalIds] = useState<Set<string>>(
    isOnboarding
      ? List(ONBOARDING_DEFAULT_CHECKED_IDS).toSet()
      : user
          .getStandardSetCreators()
          .filter((preference) => preference.type === 'national' || preference.type === 'internal')
          .map((standardSetCreator) => standardSetCreator.getId())
          .toSet()
  );

  // IDs of actual curriculum areas the user has selected
  const [userCurriculumAreas, setUserCurriculumAreas] = useState<Set<string>>(
    user
      .getCurriculumAreas()
      .map((curriculumArea) => curriculumArea.getId())
      .toSet()
  );

  const [hasCurriculumAreaError, setHasCurriculumAreaError] = useState(false);

  // ID for the ONE state standard creator the user can select from the dropdown
  const [userStateId, setUserStateId] = useState<string>();

  // On the DB, national and state standard creators are stored the same way, in the UI,
  // we are splitting out the state standards into the dropdown, and the national standards
  // into the checkbox list.
  const groupStandardSetCreators = (standardSetCreators) => {
    const groupedStandardCreators = standardSetCreators.groupBy((creator) => creator.type);
    setNationalPreferences(
      /// with national preferences, we're including the internal Albert standards
      groupedStandardCreators
        .get('national')
        .concat(groupedStandardCreators.get('internal', List()))
        .sort()
    );
    // sort state standard creators before setting value so they show up in the dropdown correctly
    setStatePreferences(
      groupedStandardCreators.get('state').sortBy((preference) => preference.name)
    );
  };

  // fetch all curriculum areas and standard set publishers on page load
  useEffect(() => {
    const fetchOptions = async () => {
      const [allStandardSetCreators, allCurriculumAreas] = (await Promise.all([
        allStandardSetCreatorsQuery.getResourcePromise(),
        allCurriculumAreasQuery.getResourcePromise()
      ])) as [List<StandardSetCreatorModelV1>, List<CurriculumAreaModelV1>];
      // handle grouping of standard preferences by state or national
      groupStandardSetCreators(allStandardSetCreators);
      setCurriculumAreaPreferences(allCurriculumAreas);
    };
    fetchOptions();
  }, []);

  // when state preferences are loaded, set user state ID if the user has a state preference
  // IF the user is onboarding, load state from their associated school
  // otherwise fall back to N/A option (userStateId is undefined) — adjusted during render
  const [prevStateSync, setPrevStateSync] = useState<{
    statePreferences?: typeof statePreferences;
    user?: typeof user;
    isOnboarding?: boolean;
  }>({});
  if (
    prevStateSync.statePreferences !== statePreferences ||
    prevStateSync.user !== user ||
    prevStateSync.isOnboarding !== isOnboarding
  ) {
    setPrevStateSync({statePreferences, user, isOnboarding});
    const userStateName = getUserState(user, isOnboarding);
    if (userStateName) {
      const userState = statePreferences.find(
        (preference) => preference.getName() === userStateName
      );
      setUserStateId(userState?.getId());
    }
  }

  const handleSetStateId = (value) => {
    // value has to be n/a here or else the select will fall back to the text value of the option if set to null or undefined
    if (value === 'N/A') {
      setUserStateId(undefined);
    } else {
      setUserStateId(value);
    }
  };

  const saveUserPreferences = async () => {
    if (userCurriculumAreas.size === 0) {
      setHasCurriculumAreaError(true);
      return;
    }
    setHasCurriculumAreaError(false);
    const preferencesToAdd = List(userNationalIds).toJS();
    if (userStateId) {
      preferencesToAdd.push(userStateId);
    }
    try {
      await user
        .replaceRelationship({type: 'standard_set_creators_v1', relation: preferencesToAdd})
        .replaceRelationship({
          type: 'curriculum_areas_v1',
          relation: List(userCurriculumAreas).toJS()
        })
        .save();
      addToast({
        color: 'positive',
        message: 'Content preferences updated'
      });
      invalidatePartialInterest({resourcePath: ['users_v2', sessionStore.getUserId()]});
      // Evict the standards tree Apollo cache so the Adaptive Subject Guide
      // reflects updated preferences without a page refresh
      apolloClient.cache.evict({id: 'ROOT_QUERY', fieldName: 'guideLevelTreeSearchByStandard'});
      apolloClient.cache.gc();
      if (isOnboarding) {
        // New flow: complete the acknowledgment with actual preferences
        const contentPreferences = {
          standardSetCreatorIds: preferencesToAdd,
          curriculumAreaIds: List(userCurriculumAreas).toJS(),
          ...(userStateId ? {stateStandardSetCreatorId: userStateId} : {})
        };
        await performSignupStep({
          contentPersonalization: {
            contentPreferences: JSON.stringify(contentPreferences)
          }
        });
        onOnboardingComplete();
      }
    } catch (error) {
      console.error(error);
      addGenericErrorToast();
    }
  };

  // sort 'Other' option to the bottom of the preferences list
  const sortedCurriculumAreaPreferences = curriculumAreaPreferences.sort((preference) =>
    preference.getName() === 'Other' ? 1 : -1
  );

  return (
    <>
      {isOnboarding && (
        <div className='mb-x4 m-auto text-center'>
          <Text data-testid='personalization__section-header' className='block' bold size='xl'>
            Verify Your Preferences
          </Text>
          <Text color='tertiary'>We&apos;ll help you find relevant content faster</Text>
        </div>
      )}
      <div
        className={classnames(
          {
            'content-personalization-body__onboarding': isOnboarding
          },
          getCardClassName({
            paddingLevel: isOnboarding ? 4 : 0,
            border: isOnboarding ? 'regular' : 'none'
          })
        )}
      >
        {!isOnboarding && (
          <>
            <div className='mt-x8'>
              <Heading className='mb-x1'>Content Preferences</Heading>
              <Text color='tertiary'>
                We&apos;ll use this information to surface only the most relevant standards and
                subjects.
              </Text>
            </div>
            <hr className='content-personalization__section-divider' />
          </>
        )}
        <PersonalizationSection
          title='Region'
          description='Your location will let us more accurately suggest standards'
        >
          <>
            <Text className='mt-x2 mb-x2 block'>In which state do you teach?</Text>
            <Select
              value={userStateId}
              onChange={(e) => handleSetStateId(e.target.value)}
              data-testid='personalization__standard-state-dropdown'
            >
              <option key='N/A' value='N/A'>
                None / Not applicable
              </option>
              {statePreferences
                .map((preference) => (
                  <option key={preference.getName()} value={preference.getId()}>
                    {preference.getName()}
                  </option>
                ))
                .toArray()}
            </Select>
          </>
        </PersonalizationSection>
        <PersonalizationSection title='Standard Groups' description='Select any that you teach.'>
          <div className='flex flex-col'>
            {nationalPreferences
              .map((preference) => (
                <div className='mb-x1 flex' key={preference.getId()}>
                  <Checkbox
                    size='s'
                    className='personalization__standard-publisher-checkbox mr-x2'
                    data-testid={`personalization__national-checkbox__${preference.getName()}`}
                    label={preference.getName()}
                    checked={userNationalIds.has(preference.getId())}
                    onChange={() =>
                      setUserNationalIds(
                        (prevIds) =>
                          (prevIds.has(preference.getId())
                            ? prevIds.delete(preference.getId())
                            : prevIds.add(preference.getId())) as Set<string>
                      )
                    }
                  />
                  <Text>{preference.getName()}</Text>
                </div>
              ))
              .toArray()}
          </div>
        </PersonalizationSection>
        <PersonalizationSection
          title='Curriculum Areas'
          hr={!isOnboarding}
          description={
            <>
              Select one or more options.
              {user.isSchoolAdministrator() && (
                <Text italic color='tertiary'>
                  {' '}
                  For admins, we recommend selecting all curriculum areas.
                </Text>
              )}
            </>
          }
        >
          {hasCurriculumAreaError && <CurriculumAreaErrorBanner />}
          <div className='flex flex-col'>
            {sortedCurriculumAreaPreferences
              .map((curriculumArea) => (
                <div className='mb-x1 flex' key={curriculumArea.getId()}>
                  <Checkbox
                    size='s'
                    className='personalization__standard-publisher-checkbox mr-x2'
                    data-testid={`personalization__curriculum-areas-checkbox__${curriculumArea.getName()}`}
                    label={curriculumArea.getName()}
                    checked={userCurriculumAreas.has(curriculumArea.getId())}
                    onChange={() =>
                      setUserCurriculumAreas(
                        (prevIds) =>
                          (prevIds.has(curriculumArea.getId())
                            ? prevIds.delete(curriculumArea.getId())
                            : prevIds.add(curriculumArea.getId())) as Set<string>
                      )
                    }
                  />
                  <Text>
                    {curriculumArea.getName()}
                    {curriculumArea.getName() === 'Other' && ' (Not listed)'}
                  </Text>
                </div>
              ))
              .toArray()}
          </div>
        </PersonalizationSection>
      </div>
      <div
        className={classnames(
          'flex justify-end',
          {
            'content-personalization-body__onboarding': isOnboarding
          },
          'mt-x4'
        )}
      >
        <Button
          data-testid='personalization__submit-changes-btn'
          onClick={saveUserPreferences}
          className={classnames('mb-x4', {
            'content-peronsalization-button__onboarding': isOnboarding
          })}
        >
          Save Preferences
        </Button>
      </div>
    </>
  );
};

interface PersonalizationSectionProps extends PropsWithChildrenRequired {
  title: string;
  description: React.ReactNode;
  hr?: boolean;
}
const PersonalizationSection = ({
  title,
  description,
  hr = true,
  children
}: PersonalizationSectionProps) => (
  <>
    <section className='content-personalization__section'>
      <div className='content-personalization__section-description'>
        <Text bold className='mb-x1 self-stretch'>
          {title}
        </Text>
        <Text className='self-stretch' color='tertiary'>
          {description}
        </Text>
      </div>
      <div className='content-personalization__section-content'>{children}</div>
    </section>
    {hr && <hr className='content-personalization__section-divider' />}
  </>
);

const CurriculumAreaErrorBanner = () => (
  <Banner
    type='negative'
    className='mb-x2'
    data-testid='content-personalization__curriculum-area-error-banner'
  >
    <Banner.Icon icon={['fal', 'triangle-exclamation']} />
    Select at least one option.
  </Banner>
);

export default awaitMandarkQueries(
  () => ({
    queries: {
      user: resource('users_v2')
        .mandarkEndpoint(['users_v2', sessionStore.getUserId()])
        .include('standard_set_creators_v1,curriculum_areas_v1,school_v5')
    },
    passthroughProps: {}
  }),
  ContentPersonalization
);
